import { useCallback } from "react";
import { useApiService } from "./apiService";

export interface TenantDto {
  id: string;
  name: string;
  status: string;
  logoUrl?: string;
}

export interface FeatureDto {
  id: string;
  name: string;
  description: string;
}

export interface UpsertTenantRequest {
  name: string;
  description: string;
  status: "Active" | "Inactive";
  featureKeys: string[];
  logoFile?: File | null;
}

export type MiniPackTemplateType = "Airframe" | "Engine" | "APU" | "LandingGear" | "SubAssembly";

export interface MiniPackTemplateDto {
  id: string;
  templateType: MiniPackTemplateType;
  fileName: string;
  uploadedAtUtc: string;
  uploadedBy: string;
}

export interface MiniPackTemplateSectionDto {
  id: string;
  sectionKey: string;
  sectionName: string;
  sortOrder: number;
}

export interface MiniPackTemplateSectionsResponseDto {
  templateType: string;
  fileName: string;
  parsedFromTemplate: boolean;
  sections: MiniPackTemplateSectionDto[];
}

const toTenantFormData = (request: UpsertTenantRequest): FormData => {
  const form = new FormData();

  form.append("Name", request.name);
  form.append("Description", request.description ?? "");
  form.append("Status", request.status);

  (request.featureKeys ?? []).forEach((k) => {
    if (typeof k === "string" && k.trim().length > 0) {
      form.append("FeatureKeys", k);
    }
  });

  if (request.logoFile) {
    form.append("Logo", request.logoFile);
  }

  return form;
};

export const useTenantManagementService = () => {
  const apiService = useApiService();

  const getTenants = useCallback(async (): Promise<TenantDto[]> => {
    const response = await apiService.apiCall<TenantDto[]>({
      method: "GET",
      endpoint: "/api/Tenants",
      requireAuth: true,
    });

    return response.data ?? [];
  }, [apiService]);

  const getAvailableFeatures = useCallback(async (): Promise<FeatureDto[]> => {
    const response = await apiService.apiCall<FeatureDto[]>({
      method: "GET",
      endpoint: "/api/AppFeature",
      requireAuth: true,
    });

    return response.data ?? [];
  }, [apiService]);

  const getTenantFeatureKeys = useCallback(
    async (tenantId: string): Promise<string[]> => {
      const response = await apiService.apiCall<string[]>({
        method: "GET",
        endpoint: `/api/Tenants/${tenantId}/features`,
        requireAuth: true,
      });

      return response.data ?? [];
    },
    [apiService]
  );

  const addTenant = useCallback(
    async (request: UpsertTenantRequest): Promise<boolean> => {
      const formData = new FormData();

      formData.append("name", request.name);
      formData.append("description", request.description);
      formData.append("status", request.status);

      request.featureKeys.forEach((f) => formData.append("featureKeys", f));

      if (request.logoFile) {
        formData.append("logo", request.logoFile);
      }

      const response = await apiService.apiCall<boolean>({
        method: "POST",
        endpoint: "/api/Tenants",
        payload: toTenantFormData(request),
        requireAuth: true,
      });

      return response?.data === true;
    },
    [apiService]
  );

  const updateTenant = useCallback(
    async (tenantId: string, request: UpsertTenantRequest): Promise<boolean> => {
      const formData = new FormData();

      formData.append("name", request.name);
      formData.append("description", request.description);
      formData.append("status", request.status);

      request.featureKeys.forEach((f) => formData.append("featureKeys", f));

      if (request.logoFile) {
        formData.append("logo", request.logoFile);
      }

      const response = await apiService.apiCall<boolean>({
        method: "PUT",
        endpoint: `/api/Tenants/${tenantId}`,
        payload: toTenantFormData(request),
        requireAuth: true,
      });

      return response?.data === true;
    },
    [apiService]
  );

  const deleteTenant = useCallback(
    async (tenantId: string): Promise<boolean> => {
      const response = await apiService.apiCall<boolean>({
        method: "DELETE",
        endpoint: `/api/Tenants/${tenantId}`,
        requireAuth: true,
      });

      return response?.data === true;
    },
    [apiService]
  );

  const getMiniPackTemplates = useCallback(async (): Promise<MiniPackTemplateDto[]> => {
    const response = await apiService.apiCall<MiniPackTemplateDto[]>({
      method: "GET",
      endpoint: "/api/mini-pack-templates",
      requireAuth: true,
    });

    return response.data ?? [];
  }, [apiService]);

  const getMiniPackTemplateSections = useCallback(
    async (templateType: MiniPackTemplateType): Promise<MiniPackTemplateSectionsResponseDto> => {
      const response = await apiService.apiCall<MiniPackTemplateSectionsResponseDto>({
        method: "GET",
        endpoint: `/api/mini-pack-templates/sections/${encodeURIComponent(templateType)}`,
        requireAuth: true,
      });

      return (
        response.data ?? {
          templateType,
          fileName: "",
          parsedFromTemplate: false,
          sections: [],
        }
      );
    },
    [apiService]
  );

  const uploadMiniPackTemplate = useCallback(
    async (templateType: MiniPackTemplateType, file: File): Promise<MiniPackTemplateDto> => {
      const formData = new FormData();
      formData.append("TemplateType", templateType);
      formData.append("File", file);

      const response = await apiService.apiCall<MiniPackTemplateDto>({
        method: "POST",
        endpoint: "/api/mini-pack-templates",
        payload: formData,
        requireAuth: true,
      });

      return response.data;
    },
    [apiService]
  );

  const getMiniPackTemplateContent = useCallback(
    async (templateId: string): Promise<string> => {
      const response = await apiService.getBlob(`/api/mini-pack-templates/${templateId}/download`, true);
      return response.blob.text();
    },
    [apiService]
  );

  return {
    getTenants,
    getAvailableFeatures,
    getTenantFeatureKeys,
    addTenant,
    updateTenant,
    deleteTenant,
    getMiniPackTemplates,
    getMiniPackTemplateSections,
    uploadMiniPackTemplate,
    getMiniPackTemplateContent,
  };
};